import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { Link, generatePath, useLocation, useParams } from "react-router-dom";

import { useQuery } from "@apollo/client";
import { Button, Status } from "@snapchat/snap-design-system";
import { clsx } from "clsx";
import { FormikProvider, useFormik } from "formik";
import { useIntl } from "react-intl";

import { myLensesActionType } from "../../analytics/event-creators/createActionEvent";
import { useBlizzardEvents } from "../../analytics/hooks/useBlizzardEvents";
import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { AbsoluteRoute, type RouteParams } from "../../core/routes";
import { useLensManagementRoutes } from "../../core/routes/hooks";
import { CofConfigId, LensCreatorPayoutEligibility, SurfaceType, Visibility } from "../../gql-generated/types";
import { Card, CardTitle, PageHeader, PageSubHeader } from "../../layout/components";
import { PageContentSpinner } from "../../layout/components/PageContentSpinner";
import { LOCKED_BASE_STATUSES } from "../../lens/state/makeLensActionsConfigBasedOnStatus";
import { ExclusiveLensValidationAlert } from "../../lens-creator-payout/components/ExclusiveLensValidationAlert";
import { LensPreview } from "../../lens-preview/components/LensPreview";
import { useLensPreviewState } from "../../lens-preview/hooks";
import { GAME_LENS_VIDEO_DURATION_REQUIREMENT } from "../../lens-preview/validation";
import { useToggleState } from "../../shared/hooks";
import {
    type LensPreviewMediaItem,
    convertMyLensesPreviewMediaToItems,
    mapPreviewMediaItemsToResources,
} from "../../shared/state";
import { getExclusiveLensFlags } from "../../shared/utils/monetizationUtils";
import { InvalidPreviewBadge } from "../../submit/components/lens-details/shared/messages.generated";
import { useGameLensToggle } from "../../submit/components/lens-details/shared/useGameLensToggle";
import { validateExclusiveLens } from "../../submit/validation";
import { getWebLensForUpdate } from "../api/queries";
import {
    type GetWebLensForUpdateQuery,
    type GetWebLensForUpdateQueryVariables,
} from "../api/queries/getWebLensForUpdate.gql-generated";
import { useUpdateWebLens } from "../hooks/useUpdateWebLens";
import { useWebLensBuildOutputMeasurement } from "../hooks/useWebLensBuildOutputMeasurement";
import { useWebLensPreviewValidation } from "../hooks/useWebLensPreviewValidation";
import { useWebLensStartMode } from "../hooks/useWebLensStartMode";
import { useWebLensVisibilityConfig } from "../hooks/useWebLensVisibilityConfig";
import { LOGO_MAX_BYTES, LOGO_MAX_DIMENSION_PX, type SubmissionCheck } from "../model";
import {
    buildBudgetCheck,
    buildLogoChecks,
    buildPackageSizeChecks,
    describePackageTypeFailure,
    formatMegabytes,
    isBuildOutputWithinBudget,
    isLogoAccepted,
    isPackageSizeAccepted,
    isZipPackage,
} from "../validation/preChecks";
import { resolveReadinessState } from "../validation/readinessChecks";
import { type WebLensUpdateFormValues, webLensUpdateFormSchema } from "../validation/webLensFormSchemas";

import { SubmissionChecklist } from "./SubmissionChecklist";
import styles from "./WebLensPage.module.css";
import { WebLensPublishModal } from "./WebLensPublishModal";
import { WebLensGameLensToggle } from "./fields/WebLensGameLensToggle";
import { WebLensIconControl } from "./fields/WebLensIconControl";
import { WebLensNameField } from "./fields/WebLensNameField";
import { WebLensPackageDropzone } from "./fields/WebLensPackageDropzone";
import {
    ChangeUpdateTarget,
    DetailsSectionTitle,
    PackageSectionTitle,
    RailTitle,
    RenameRailHelper,
    ReplaceLogoCta,
    RequirementPreviewGame,
    RequirementPreviewVideo,
    UpdateCta,
    UpdateLogoHint,
    UpdatePageSubtitle,
    UpdatePageTitle,
    UpdateRailHelper,
    UpdateTarget,
    UpdateTargetId,
    UpdatingCta,
    messages,
} from "./messages.generated";

const SNAPCHAT_SURFACE = [SurfaceType.SurfaceTypeSnapchat] as const;

/**
 * Update an existing lens with a web-app package, or rename it without one.
 *
 * A changed name with nothing else attached goes through the generic updateLens mutation: Lens
 * Central cuts a new version on the current package (no conversion; a published lens is reviewed
 * again). Refused while the latest version failed, since the new version would inherit and re-run
 * it. An icon or a game marking still needs a package.
 */
export const WebLensUpdatePage: FC = () => {
    const intl = useIntl();
    const { orgId = "", lensId = "" } = useParams<RouteParams[AbsoluteRoute.WEB_LENSES_UPDATE]>();
    const location = useLocation();
    // Arrived from the update picker exactly when the picker's mode is still in the URL.
    const isPickerReferrer = useWebLensStartMode().mode === "update";
    const { sendBlizzardEvent } = useBlizzardEvents();
    const [file, setFile] = useState<File | null>(null);
    const buildOutput = useWebLensBuildOutputMeasurement(file);
    const [logo, setLogo] = useState<File | null>(null);
    // Marking only: the toggle renders while the lens is not yet a game (this maskless path can set
    // the flag but never clear it — see UpdateWebLensRequest.isGameUserProvided).
    const [markAsGame, setMarkAsGame] = useState(false);
    const { handleGameLensToggle } = useGameLensToggle({ setIsGameUserProvided: setMarkAsGame });
    const { checks, result, updating, update, rename, reset } = useUpdateWebLens();
    const { goToLensPage } = useLensManagementRoutes();
    const { configs } = useFeatureGates();
    const isMyLensesCreatorPayoutEnabled = configs[CofConfigId.MyLensesCreatorPayout].enabled;
    const [publishModalProps, showPublishModal] = useToggleState();
    const [creatorPayoutEdit, setCreatorPayoutEdit] = useState<{ lensId: string; enrolled: boolean } | null>(null);
    const [visibilityEdit, setVisibilityEdit] = useState<{ lensId: string; visibility: Visibility } | null>(null);
    const onCreatorPayoutEnrolledChange = useCallback(
        (enrolled: boolean) => {
            setCreatorPayoutEdit({ lensId, enrolled });
        },
        [lensId],
    );
    // Hand off to the lens page once the new version exists: that is where conversion progress and
    // a rejection of the new archive show up. Keyed on the id rather than on update() returning, so
    // a failed update stays put with its rail intact.
    useEffect(() => {
        if (result?.lensId) {
            goToLensPage(result.lensId);
        }
    }, [goToLensPage, result?.lensId]);

    const lensQuery = useQuery<GetWebLensForUpdateQuery, GetWebLensForUpdateQueryVariables>(getWebLensForUpdate, {
        variables: { lensId },
        skip: lensId.length === 0,
    });
    const lens = lensQuery.data?.getLens.lens;
    const isDraft = lens?.visibility === Visibility.VisibilityDontPublishToSnapchat;
    const { visibilityConfig } = useWebLensVisibilityConfig({
        lensFolderId: lens?.lensAccountId?.lensSpaceId ?? "",
        lensForUpdate: lens
            ? { visibility: lens.visibility, exclusiveLensStatus: lens.exclusiveLensStatus }
            : undefined,
    });
    const permittedPublishedVisibilities = useMemo(
        () => visibilityConfig.communityDistributionOptions ?? [],
        [visibilityConfig.communityDistributionOptions],
    );
    const editedVisibility = visibilityEdit?.lensId === lensId ? visibilityEdit.visibility : lens?.visibility;
    // A permission refresh can narrow the options while this page is open. Fall back to the
    // server value instead of retaining a now-disallowed edit.
    const selectedVisibility =
        editedVisibility && permittedPublishedVisibilities.includes(editedVisibility)
            ? editedVisibility
            : lens?.visibility;
    const onVisibilityChange = useCallback(
        (visibility: Visibility) => {
            if (
                !permittedPublishedVisibilities.includes(visibility) ||
                visibilityConfig.disabledOptions?.[visibility]
            ) {
                return;
            }
            setVisibilityEdit({ lensId, visibility });
        },
        [lensId, permittedPublishedVisibilities, visibilityConfig.disabledOptions],
    );
    const visibilityChange =
        lens && selectedVisibility !== undefined && selectedVisibility !== lens.visibility
            ? selectedVisibility
            : undefined;
    const hasExclusiveLensProgram = lens
        ? getExclusiveLensFlags(lens.exclusiveLensStatus).hasExclusiveLensProgram
        : false;
    const isStoredCreatorPayoutEnrolled = Boolean(
        lens && lens.lensCreatorPayoutEligibility !== LensCreatorPayoutEligibility.LensCreatorPayoutEligibilityUnset,
    );
    const canEditCreatorPayout =
        isMyLensesCreatorPayoutEnabled && !isDraft && !hasExclusiveLensProgram && !isStoredCreatorPayoutEnrolled;
    // An untouched field must stay absent: false would explicitly unenroll on update.
    const creatorPayoutChange =
        canEditCreatorPayout &&
        creatorPayoutEdit?.lensId === lensId &&
        creatorPayoutEdit.enrolled !== isStoredCreatorPayoutEnrolled
            ? creatorPayoutEdit.enrolled
            : undefined;
    const isCreatorPayoutEnrolled = creatorPayoutChange ?? isStoredCreatorPayoutEnrolled;

    // Lens Central rejects updates while any version processes (the lens is locked); holding
    // Submit here turns that server rejection into a visible page state instead.
    const isLensLocked = Boolean(lens && LOCKED_BASE_STATUSES.has(lens.status));
    // True for an Invalid or Rejected latest version; a rename would inherit and re-run it.
    const latestVersionFailed = Boolean(lens?.rejectionErrors?.hasSubmissionErrors);

    // The lens' own previews, in the editor's vocabulary — the starting point for the gameplay
    // video a marking requires. Kept items travel back by mediaId, so leaving them alone is
    // lossless; isVideo comes from the stored URL, which is how the Studio re-submission reads
    // them too.
    const storedPreviewItems = useMemo(
        () => convertMyLensesPreviewMediaToItems(lens?.previewMediaList ?? []),
        [lens?.previewMediaList],
    );
    const [previewMediaItems, setPreviewMediaItems] = useState<LensPreviewMediaItem[]>([]);
    // Seeded once the query lands (and again only if the lens itself changes) rather than derived,
    // because from then on the editor owns this list.
    useEffect(() => {
        setPreviewMediaItems(storedPreviewItems);
    }, [storedPreviewItems]);

    const { uploadFileLoading, ...lensPreviewProps } = useLensPreviewState({
        previewMediaItems,
        setPreviewMediaItems,
        videoDurationRequirement: GAME_LENS_VIDEO_DURATION_REQUIREMENT,
        videoOnly: true,
    });
    const { errorMessage: previewError, hasVideoPreview } = useWebLensPreviewValidation({
        previewMediaItems,
        previewValidationMessage: lensPreviewProps.validationMessage,
    });
    const visiblePreviewError = lensPreviewProps.isTouched ? previewError : null;
    // Omit unchanged previews so Lens Central keeps the stored media.
    const havePreviewsChanged =
        previewMediaItems.length !== storedPreviewItems.length ||
        previewMediaItems.some((item, index) => item.url !== storedPreviewItems[index]?.url);
    const needsPackage = file !== null || logo !== null || markAsGame || havePreviewsChanged;
    const canEditPreviews = needsPackage || hasExclusiveLensProgram;
    const canRenameAlone = !needsPackage && !latestVersionFailed;
    const exclusiveLensValidation = validateExclusiveLens({ previewMediaItems, visibility: selectedVisibility });
    const hasExclusiveLensValidationError = hasExclusiveLensProgram && !exclusiveLensValidation.isValid;

    // The current name arrives with the lens query, so the creator edits from it rather than a
    // blank field — enableReinitialize is what applies it once the query lands.
    const formik = useFormik<WebLensUpdateFormValues>({
        initialValues: { name: lens?.name ?? "" },
        validationSchema: webLensUpdateFormSchema,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: (formValues) => {
            if (!lens) {
                return;
            }
            // Only an actual rename travels; matching the current name means "keep". Same for the
            // icon: no replacement attached means the previous version's icon is inherited.
            const trimmedName = formValues.name.trim();
            const name = trimmedName === lens.name ? null : trimmedName;
            if (!file) {
                if (name === null || !canRenameAlone) {
                    return;
                }
                sendBlizzardEvent({
                    event: "MYLENSES_ACTION",
                    actionType: myLensesActionType.submit,
                    label: "web_lens_rename",
                    namespace: "lenses",
                });
                void rename({
                    lensId: lens.id,
                    name,
                    ...(creatorPayoutChange === undefined ? {} : { lensCreatorPayoutEnrolled: creatorPayoutChange }),
                    ...(visibilityChange === undefined ? {} : { visibility: visibilityChange }),
                });
                return;
            }
            sendBlizzardEvent({
                event: "MYLENSES_ACTION",
                actionType: myLensesActionType.submit,
                label: "web_lens_update",
                namespace: "lenses",
            });
            void update({
                file,
                lensId: lens.id,
                logo,
                name,
                markAsGame,
                previewMediaResources: havePreviewsChanged ? mapPreviewMediaItemsToResources(previewMediaItems) : null,
                buildOutput,
                ...(creatorPayoutChange === undefined ? {} : { lensCreatorPayoutEnrolled: creatorPayoutChange }),
                ...(visibilityChange === undefined ? {} : { visibility: visibilityChange }),
            });
        },
    });

    const { values, isValid, submitForm } = formik;
    const isPackageReady =
        Boolean(file && isZipPackage(file) && isPackageSizeAccepted(file)) && isBuildOutputWithinBudget(buildOutput);
    const currentName = lens?.name ?? "";
    const hasRename = values.name.trim() !== currentName;
    const isRenameOnly = !needsPackage && hasRename;
    // Same contract as submission: every row the rail can turn red also disables the button, so a
    // red row never sits next to an enabled button that fails for exactly that reason. The name
    // rules live in the Formik schema (isValid); an unusable replacement icon blocks, while no
    // replacement is fine, since the current icon is simply kept.
    const canUpdate =
        (isPackageReady || (hasRename && canRenameAlone)) &&
        isValid &&
        (!logo || isLogoAccepted(logo)) &&
        (!needsPackage || (!previewError && hasVideoPreview)) &&
        !isLensLocked &&
        (!file || !hasExclusiveLensValidationError) &&
        !updating &&
        // A newly picked preview does not enter previewMediaItems until its upload ref settles.
        // Keep Update disabled so an in-flight upload cannot be silently omitted from the mutation.
        !uploadFileLoading;
    const onConfirmUpdate = useCallback(() => {
        if (canUpdate) {
            void submitForm();
        }
    }, [canUpdate, submitForm]);
    const onUpdate = useCallback(() => {
        if (!canUpdate) {
            return;
        }
        if (isDraft) {
            void submitForm();
            return;
        }
        showPublishModal();
    }, [canUpdate, isDraft, submitForm, showPublishModal]);
    const failedCheck = checks.find((check) => check.state === "fail");

    // Readiness rows, ordered like runClientPreChecks so they keep their places when update() takes
    // the rail over. Rename-only shows one row instead (same id as rename()'s progress row).
    const describeRenameOnly = (): Pick<SubmissionCheck, "state" | "detail"> => {
        if (latestVersionFailed) {
            return { state: "fail", detail: intl.formatMessage(messages.checkRenameBlockedFailedUpdateDetail) };
        }
        if (!isValid) {
            return { state: "fail" };
        }
        return {
            state: "pass",
            detail: intl.formatMessage(messages.checkRenameDetail, {
                current: currentName,
                name: values.name.trim(),
            }),
        };
    };
    const renameOnlyChecks: SubmissionCheck[] = [
        { id: "rename", label: intl.formatMessage(messages.checkRenameLabel), ...describeRenameOnly() },
    ];
    const packageChecks: SubmissionCheck[] = [
        {
            id: "package-type",
            label: intl.formatMessage(messages.checkPackageTypeLabel),
            state: file ? (isZipPackage(file) ? "pass" : "fail") : "pending",
            detail: file ? describePackageTypeFailure(intl, file) : undefined,
        },
        ...(file ? buildPackageSizeChecks(intl, file) : []),
        ...(buildOutput ? [buildBudgetCheck(intl, buildOutput)] : []),
        ...(logo ? buildLogoChecks(intl, logo) : []),
        {
            id: "preview",
            label: intl.formatMessage(messages.checkPreviewLabel),
            state: resolveReadinessState(hasVideoPreview, lensPreviewProps.isTouched),
            detail: visiblePreviewError ?? undefined,
        },
    ];
    const readinessChecks = isRenameOnly ? renameOnlyChecks : packageChecks;
    const railChecks = checks.length > 0 ? checks : readinessChecks;

    if (lensQuery.loading) {
        return <PageContentSpinner />;
    }

    return (
        <div className={styles.page}>
            <PageHeader>
                <UpdatePageTitle />
            </PageHeader>
            <PageSubHeader>
                <UpdatePageSubtitle />
            </PageSubHeader>
            {hasExclusiveLensValidationError ? (
                <ExclusiveLensValidationAlert type="error" errorMessage={exclusiveLensValidation.errorMessage} />
            ) : null}

            {lens ? (
                <FormikProvider value={formik}>
                    <div className={styles.columns}>
                        <div className={styles.mainColumn}>
                            <Card>
                                <div className={styles.cardBody}>
                                    <div className={styles.destinationRow} data-testid="web-lens-update-target">
                                        <span>
                                            <UpdateTarget
                                                name={<span className={styles.destinationName}>{lens.name}</span>}
                                            />
                                            &nbsp;
                                            <span className={styles.packageMeta}>
                                                <UpdateTargetId lensId={lens.id} />
                                            </span>
                                        </span>
                                        {/* Back to the picker with its selection intact, so it opens on
                                            the same folder's list — the counterpart of the submit page's
                                            "Change" link. Only offered when the creator arrived from the
                                            picker; a bare deep link has no list to return to. */}
                                        {isPickerReferrer ? (
                                            <Link
                                                className={clsx(styles.textLink, styles.destinationChange)}
                                                to={{
                                                    pathname: generatePath(AbsoluteRoute.WEB_LENSES, { orgId }),
                                                    search: location.search,
                                                }}
                                            >
                                                <ChangeUpdateTarget />
                                            </Link>
                                        ) : null}
                                    </div>

                                    {isLensLocked ? (
                                        <div className={styles.chooserError} data-testid="web-lens-update-locked">
                                            {intl.formatMessage(messages.updateLensLocked)}
                                        </div>
                                    ) : null}

                                    {/* Up front, so the creator knows a package is needed before typing. */}
                                    {latestVersionFailed ? (
                                        <div
                                            className={styles.chooserError}
                                            data-testid="web-lens-update-latest-failed"
                                        >
                                            {intl.formatMessage(messages.updateLatestVersionFailed)}
                                        </div>
                                    ) : null}

                                    <div className={styles.section}>
                                        <CardTitle>
                                            <PackageSectionTitle />
                                        </CardTitle>
                                        <WebLensPackageDropzone
                                            file={file}
                                            buildOutput={buildOutput}
                                            disabled={updating}
                                            onPick={(picked) => {
                                                setFile(picked);
                                                reset();
                                            }}
                                        />
                                    </div>

                                    <div className={styles.divider} />

                                    <div className={styles.section}>
                                        <WebLensNameField
                                            disabled={updating}
                                            hint={intl.formatMessage(messages.updateNameHint)}
                                        />

                                        <WebLensIconControl
                                            logo={logo}
                                            disabled={updating}
                                            onPick={(picked) => {
                                                setLogo(picked);
                                                reset();
                                            }}
                                            currentIconUrl={lens.iconUrl || undefined}
                                            cta={<ReplaceLogoCta />}
                                            hint={
                                                <UpdateLogoHint
                                                    limit={formatMegabytes(LOGO_MAX_BYTES)}
                                                    dimension={LOGO_MAX_DIMENSION_PX}
                                                />
                                            }
                                        />

                                        {/* Offered only while the lens is not yet a game: this path
                                            can mark but not unmark (maskless UpdateLens), so an
                                            already-marked lens gets no false affordance. */}
                                        {lens.isGameUserProvided ? null : (
                                            <WebLensGameLensToggle
                                                isGameUserProvided={markAsGame}
                                                onGameLensToggle={handleGameLensToggle}
                                            />
                                        )}
                                    </div>

                                    {/* Every package update requires a video. Keep the editor
                                        available for existing Lens+ enrollments as well. */}
                                    {canEditPreviews ? (
                                        <>
                                            <div className={styles.divider} />
                                            <div className={styles.section}>
                                                <CardTitle>
                                                    <DetailsSectionTitle />
                                                </CardTitle>
                                                <div
                                                    className={styles.previewColumn}
                                                    data-testid="web-lens-preview-editor"
                                                >
                                                    <LensPreview
                                                        {...lensPreviewProps}
                                                        uploadFileLoading={uploadFileLoading}
                                                        surfaceType={SNAPCHAT_SURFACE}
                                                        disabled={updating}
                                                        badgeText={
                                                            visiblePreviewError ? <InvalidPreviewBadge /> : undefined
                                                        }
                                                        badgeStatus={visiblePreviewError ? Status.ERROR : undefined}
                                                        videoDurationSpec={
                                                            markAsGame ? (
                                                                <RequirementPreviewGame />
                                                            ) : (
                                                                <RequirementPreviewVideo />
                                                            )
                                                        }
                                                    />
                                                    {visiblePreviewError ? (
                                                        <p
                                                            className={styles.previewError}
                                                            data-testid="web-lens-preview-error"
                                                        >
                                                            {visiblePreviewError}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </div>
                                        </>
                                    ) : null}
                                </div>
                            </Card>
                        </div>

                        <div className={styles.rail}>
                            <Card>
                                <div className={styles.railBody}>
                                    <CardTitle>
                                        <RailTitle />
                                    </CardTitle>
                                    <SubmissionChecklist checks={railChecks} />
                                    <div className={styles.divider} />
                                    <div className={styles.railHelper}>
                                        {isRenameOnly ? <RenameRailHelper /> : <UpdateRailHelper />}
                                    </div>
                                    <div className={styles.submitButton}>
                                        <Button
                                            type="primary"
                                            isDisabled={!canUpdate}
                                            onClick={onUpdate}
                                            data-testid="web-lens-update-submit"
                                        >
                                            {updating ? <UpdatingCta /> : <UpdateCta />}
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                    {isDraft ? null : (
                        <WebLensPublishModal
                            {...publishModalProps}
                            lensName={values.name.trim()}
                            lensForUpdate={lens}
                            visibilityConfig={visibilityConfig}
                            visibility={selectedVisibility ?? lens.visibility}
                            onVisibilityChange={onVisibilityChange}
                            isMyLensesCreatorPayoutEnabled={isMyLensesCreatorPayoutEnabled}
                            isCreatorPayoutEnrolled={isCreatorPayoutEnrolled}
                            onCreatorPayoutEnrolledChange={onCreatorPayoutEnrolledChange}
                            isExclusiveLensChecked={hasExclusiveLensProgram}
                            previewMediaItems={previewMediaItems}
                            lensPreviewProps={lensPreviewProps}
                            helperMessage={isRenameOnly ? <RenameRailHelper /> : <UpdateRailHelper />}
                            onConfirm={onConfirmUpdate}
                            confirmLabel={updating ? <UpdatingCta /> : <UpdateCta />}
                            confirmIsPending={updating}
                            confirmIsDisabled={!canUpdate}
                            errorMessage={
                                failedCheck ? failedCheck.detail ?? intl.formatMessage(messages.genericFailure) : null
                            }
                        />
                    )}
                </FormikProvider>
            ) : (
                <div className={styles.chooserError}>{intl.formatMessage(messages.updateLensLoadFailed)}</div>
            )}
        </div>
    );
};
